import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "./IconButton.js";
import * as styles from "./OverflowMenu.css.js";
import { usePortalContainer } from "../theme/ThemeProvider.js";
import { MoreDotsIcon } from "../icons.js";
// A "⋮" trigger + fixed-position, right-aligned popover menu. Same interaction as
// CategoryHeader's inline "⋮" menu (outside click / Escape / scroll all dismiss it),
// pulled out here so other screens (trip topbar, template editor) can reuse it.
// The popover portals out of the trigger's subtree: a `transform` on an ancestor (the
// cards' `fadeUp` entrance keeps one, since it fills `both`) makes that ancestor the
// containing block for `position: fixed`, which otherwise drops the menu far from the
// trigger. The target is the theme root (the element carrying the theme's CSS custom
// properties), not `document.body` — outside it every `vars.*` resolves to nothing.
export const OverflowMenu = ({ label, items }) => {
    const [open, setOpen] = useState(null);
    const menuRef = useRef(null);
    const portalContainer = usePortalContainer();
    useEffect(() => {
        if (open === null)
            return undefined;
        const onDocMouseDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target))
                setOpen(null);
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape")
                setOpen(null);
        };
        const onScroll = () => setOpen(null);
        document.addEventListener("mousedown", onDocMouseDown);
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("scroll", onScroll, true);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown);
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [open]);
    const toggle = (e) => {
        e.stopPropagation();
        if (open !== null) {
            setOpen(null);
            return;
        }
        const r = e.currentTarget.getBoundingClientRect();
        setOpen({ top: r.bottom + 4, left: r.right });
    };
    return (_jsxs("span", { style: { position: "relative", display: "inline-flex" }, children: [_jsx(IconButton, { label: label, "aria-haspopup": "menu", "aria-expanded": open !== null, onClick: toggle, children: _jsx(MoreDotsIcon, {}) }), open !== null && createPortal(_jsx("div", { ref: menuRef, className: styles.menu, style: { top: open.top, left: open.left }, role: "menu", "aria-label": label, children: items.map((it) => (_jsx("button", { type: "button", role: "menuitem", className: it.danger === true ? styles.itemDanger : styles.item, onClick: (e) => {
                        // The menu can sit inside a clickable container (a template card); an
                        // item click is the menu's, never the card's. The trigger already does
                        // the same.
                        e.stopPropagation();
                        setOpen(null);
                        it.onClick();
                    }, children: it.label }, it.label))) }), 
            // No provider above us (component tests, Storybook-style renders): the body is
            // still a correct place to hang it, just an unthemed one.
            portalContainer ?? document.body)] }));
};
//# sourceMappingURL=OverflowMenu.js.map